import { FunnelTableItemDto, ReferentialDto } from 'api/funnel';
import { SortingFieldEnum } from 'api/actor';
import {
  Filter,
  Package,
  Building2,
  CheckCircle,
  XCircle,
  FileText,
  BarChart3
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { funnelsAPI, productsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';
import {
  DropdownButton,
  useActionMenu
} from 'components/navigation/Dropdown/GenericDropdownButton';
import { Sizes } from 'enums/sizes';
import { useDialog } from 'hooks/useDialog';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { PlateformesPickerForm } from 'back-office/shared/form/PlateformesPickerForm';
import { ReferentialKey } from 'api/referential';
import { useReferentialUtils } from 'features/referentials/hooks/useReferentialUtils';

interface GenericFunnelsTableProps {
  forcedFilters?: Record<string, any>;
  title?: string;
  onCreateConfig?: OnCreateConfig;
  onViewClick?: (funnel: FunnelTableItemDto) => void;
  onUnlinkFunnelClick?: (funnel: FunnelTableItemDto) => void;
  selectable?: boolean;
  enableFilters?: boolean;
  plateformeKeys?: string[];
  pageSize?: number;
}

export function GenericFunnelsTable({
  forcedFilters = {},
  title,
  onViewClick,
  onCreateConfig,
  onUnlinkFunnelClick,
  selectable = true,
  enableFilters = true,
  plateformeKeys = [],
  pageSize = 15
}: GenericFunnelsTableProps) {
  const { createMenuItem } = useActionMenu();
  const { openDialog } = useDialog();
  const { t } = useTranslation(NsKey.Common);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchFunnels = async (params: any) => {
    const response = await funnelsAPI.getFunnels({
      getGenericListCommandDto: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || '',
        sorting: params.sorting || SortingFieldEnum.CreationTimeDesc,
        filters: {
          ...forcedFilters,
          ...params.filters
        }
      }
    });

    return {
      items: response.items,
      totalItems: response.totalCount,
      totalPages: Math.ceil(response.totalCount / params.pageSize),
      currentPage: params.pageNumber
    };
  };

  const fetchFilters = async () => {
    return [
      {
        groupName: 'Statut',
        filters: [
          {
            key: 'isActive',
            label: 'Actif',
            type: 'select' as const,
            options: [{ value: 'true', label: 'Actifs uniquement' }]
          },
          {
            key: 'isActive',
            label: 'Inactif',
            type: 'select' as const,
            options: [{ value: 'false', label: 'Inactifs uniquement' }]
          }
        ]
      }
    ];
  };

  const handleView = (funnel: FunnelTableItemDto) => {
    if (onViewClick) {
      onViewClick(funnel);
    } else {
      console.log('Visualiser le tunnel:', funnel);
    }
  };

  const { getReferentialCodeById } = useReferentialUtils();

  const linkPlateformesToFunnel = useCallback(
    async (funnelId: string, selectedCodes: string[]) => {
      const plateformeIds = selectedCodes.flatMap(code => {
        const id = getReferentialCodeById(ReferentialKey.Plateforme, code);
        return id ? [id] : [];
      });
      await funnelsAPI.linkPlateformes({
        funnelId,
        genericLinkPlateformesCommand: { plateformeIds }
      });
    },
    [getReferentialCodeById]
  );

  const openLinkPlateformesForm = useCallback(
    async (funnel: FunnelTableItemDto) => {
      // Règle métier : plateformes du tunnel ⊆ plateformes de son produit.
      // Dans la vue produit, plateformeKeys porte déjà cet ensemble ; sinon on
      // le charge depuis le produit du tunnel.
      let includeCodes = plateformeKeys.length > 0 ? plateformeKeys : undefined;
      if (!includeCodes && funnel.product?.id) {
        try {
          const res = await productsAPI.getPlateformes({
            productId: funnel.product.id
          });
          includeCodes = (res.plateformes ?? []).map(p => p.code);
        } catch (error) {
          console.error(
            'Impossible de charger les plateformes du produit',
            error
          );
        }
      }
      openDialog(
        {
          title: t('PLATEFORME_PICKER.MANAGE_FUNNEL'),
          size: Sizes['3xl']
        },
        <PlateformesPickerForm
          includeCodes={includeCodes}
          initialSelectedCodes={funnel.plateformes?.map(org => org.code) ?? []}
          onSubmit={(selectedCodes: string[]) =>
            linkPlateformesToFunnel(funnel.id, selectedCodes)
          }
          onSuccess={() => setRefreshKey(k => k + 1)}
        />
      );
    },
    [openDialog, t, plateformeKeys, linkPlateformesToFunnel]
  );

  const handleUnlinkFunnel = useCallback(
    (funnel: FunnelTableItemDto) => {
      if (onUnlinkFunnelClick) {
        onUnlinkFunnelClick(funnel);
      } else {
        console.log('Délier le tunnel:', funnel);
      }
    },
    [onUnlinkFunnelClick]
  );

  const createMenuItems = useCallback(
    (funnel: FunnelTableItemDto) => {
      const menuItems = [
        createMenuItem(
          'managePlateformes',
          'Gérer les plateformes',
          data => {
            openLinkPlateformesForm(data.row);
          },
          {
            icon: 'building2',
            data: {
              row: funnel,
              funnelId: funnel.id
            }
          }
        ),
        createMenuItem(
          funnel.isActive ? 'disableFunnel' : 'enableFunnel',
          funnel.isActive ? 'Désactiver le tunnel' : 'Activer le tunnel',
          data => {
            handleUnlinkFunnel(data.row);
          },
          {
            icon: funnel.isActive ? 'linkOff' : 'link',
            data: {
              row: funnel,
              funnelId: funnel.id
            }
          }
        )
      ];

      return menuItems;
    },
    [createMenuItem, openLinkPlateformesForm, handleUnlinkFunnel]
  );

  const tableContent = (
    <ServerTable<FunnelTableItemDto>
      refreshTrigger={refreshKey}
      // Configuration API
      apiConfig={{
        fetchData: fetchFunnels,
        fetchFilters: enableFilters ? fetchFilters : undefined
      }}
      // Colonnes
      columns={[
        {
          key: 'label',
          label: 'Tunnel',
          sortable: true,
          width: '250px',
          render: (_value: any, row: FunnelTableItemDto) => (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Filter className="text-purple-500" size={18} />
                <span className="font-medium text-gray-900">{row.label}</span>
              </div>
              <span className="text-xs text-gray-500">Code: {row.code}</span>
              {row.branchName && (
                <span className="text-xs text-gray-500">
                  Branche: {row.branchName}
                </span>
              )}
            </div>
          )
        },
        {
          key: 'product',
          label: 'Produit',
          render: (value: any) => {
            const product = value as ReferentialDto;
            return (
              <div className="flex items-center gap-2">
                <Package size={14} className="text-blue-500" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-900">
                    {product.label}
                  </span>
                  <span className="text-xs text-gray-500">{product.code}</span>
                </div>
              </div>
            );
          }
        },
        {
          key: 'plateformes',
          label: 'Plateformes',
          render: (value: any) => {
            const plateformes = value as Array<ReferentialDto>;
            if (!plateformes || plateformes.length === 0) {
              return <span className="text-xs text-gray-400">Aucune</span>;
            }

            return (
              <div className="flex flex-col gap-1">
                {plateformes.slice(0, 2).map(org => (
                  <div key={org.id} className="flex items-center gap-1">
                    <Building2 size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-700">{org.label}</span>
                  </div>
                ))}
                {plateformes.length > 2 && (
                  <span className="text-xs text-gray-500">
                    +{plateformes.length - 2} autre(s)
                  </span>
                )}
              </div>
            );
          }
        },
        {
          key: 'activeContractsCount',
          label: 'Contrats actifs',
          align: 'center',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              <FileText size={14} className="text-green-500" />
              <span className="text-sm font-medium text-gray-900">{value}</span>
            </div>
          )
        },
        {
          key: 'quotesCount',
          label: 'Devis',
          align: 'center',
          sortable: true,
          render: (value: any) => (
            <div className="flex items-center justify-center gap-2">
              <BarChart3 size={14} className="text-orange-500" />
              <span className="text-sm font-medium text-gray-900">{value}</span>
            </div>
          )
        },
        {
          key: 'isActive',
          label: 'Statut',
          align: 'center',
          sortable: true,
          render: (value: any) => (
            <div className="flex justify-center">
              {value ? (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 inline-flex items-center gap-1">
                  <CheckCircle size={12} />
                  Actif
                </span>
              ) : (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200 inline-flex items-center gap-1">
                  <XCircle size={12} />
                  Inactif
                </span>
              )}
            </div>
          )
        }
      ]}
      title={
        title
          ? {
              label: title,
              iconKey: 'filter'
            }
          : undefined
      }
      actions={{
        create: onCreateConfig,
        row: [
          {
            label: 'moreMenu',
            render: (row: FunnelTableItemDto, index: number) => (
              <DropdownButton
                key={index}
                width="w-64"
                anchor="bottom"
                items={createMenuItems(row)}
              />
            )
          }
        ]
      }}
      selectable={selectable}
      pageSize={pageSize}
      pageSizeOptions={[10, 15, 20, 30, 50]}
      showPaginationInfo
      enableSearch
      enableFilters={enableFilters}
      searchPlaceholder="Rechercher un tunnel (libellé, code, produit...)"
      skeletonRowCount={10}
      onRowClick={handleView}
    />
  );

  return tableContent;
}
